import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { LISTEN, FOLLOW } from "../lib/site";
import { platformIconMap } from "./BrandIcons";
import { trackClick } from "../lib/analytics";
import { useSite } from "../context/SiteContext";

const fmt = (n) => (n ? new Intl.NumberFormat("fr-FR").format(n) : null);

const Row = ({ item, index, cta }) => {
  const Icon = platformIconMap[item.id];
  return (
    <motion.a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackClick(item.id)}
      data-testid={`platform-card-${item.id}-${cta.toLowerCase()}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className="group flex items-center justify-between backdrop-blur-2xl bg-white/[0.02] border border-white/[0.08] hover:border-gold/40 px-6 py-5 transition-colors duration-300"
    >
      <div className="flex items-center gap-4">
        {Icon && <Icon size={24} className="text-white/80 group-hover:text-gold transition-colors duration-300" />}
        <div>
          <p className="text-white font-medium leading-tight">{item.name}</p>
          {item.handle && <p className="text-xs text-[#8A8A8A]">{item.handle}</p>}
        </div>
      </div>
      <span className="flex items-center gap-1.5 text-sm text-gold uppercase tracking-widest">
        {cta}
        <ArrowUpRight className="w-4 h-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-300" />
      </span>
    </motion.a>
  );
};

export const Platforms = () => {
  const site = useSite();
  const stats = site?.stats || {};

  const listen = LISTEN.map((p) => {
    if (p.id !== "spotify") return p;
    const parts = [
      fmt(stats.spotify_listeners) && `${fmt(stats.spotify_listeners)} auditeurs mensuels`,
      fmt(stats.myturn_streams) && `${fmt(stats.myturn_streams)} écoutes`,
    ].filter(Boolean);
    return parts.length ? { ...p, handle: parts.join(" · ") } : p;
  });

  const followCounts = { instagram: stats.instagram, tiktok: stats.tiktok, youtube: stats.youtube };
  const follow = FOLLOW.map((p) => {
    const c = followCounts[p.id];
    return c ? { ...p, handle: `${p.handle} · ${fmt(c)} abonnés` } : p;
  });
  const hasCounts = Object.values(followCounts).some((c) => c);

  return (
    <section id="platforms" data-testid="platforms-section" className="py-24 md:py-32 lg:py-40 relative">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="mb-14">
          <span className="text-xs sm:text-sm uppercase tracking-[0.3em] text-gold">Restez proches de moi</span>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 leading-tight max-w-2xl">
            Écoutez ma musique. <span className="gold-text">Suivez mon aventure.</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Listen */}
          <div>
            <h3 className="font-serif text-2xl text-white mb-6 flex items-center gap-3">
              <span className="text-gold text-sm tracking-[0.3em] uppercase">01</span> Écouter « My Turn »
            </h3>
            <div className="space-y-3">
              {listen.map((p, i) => (
                <Row key={p.id} item={p} index={i} cta={p.cta} />
              ))}
            </div>
          </div>

          {/* Follow */}
          <div>
            <h3 className="font-serif text-2xl text-white mb-6 flex items-center gap-3">
              <span className="text-gold text-sm tracking-[0.3em] uppercase">02</span> Me suivre
            </h3>
            <div className="space-y-3">
              {follow.map((p, i) => (
                <Row key={p.id} item={p} index={i} cta="Suivre" />
              ))}
            </div>
            {!hasCounts && (
              <p className="text-xs text-[#8A8A8A] mt-6 leading-relaxed">
                Les compteurs d'abonnés s'affichent ici dès que les chiffres sont renseignés, aucun chiffre inventé.
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
